import { Button, Col, Row, Space, Spin, Tooltip, notification } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import socketIOClient from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import BreadCrumb from '../../modules/breadcrumbs';
import {
  doDispatchRoutePlan,
  doGetRoutePlan,
  doOptimizeRoutePlan,
  doRemoveOrdersInsideRoutePlan,
  doRemoveRidersInsideRoutePlan,
  doSwapOrdersToRiderRoutePlan,
} from '../../modules/route-plans/stores/thunk';
import RoutePlanLeft from './RoutePlans/RoutePlanLeft';
import withPage from './withPage';

import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { ReactSVG } from 'react-svg';
import {
  COLORS,
  COLOR_DEFAULT,
  ONE_HOUR_PX_DEFAULT,
  ORDER_STATUSES,
  ROUTE_PLAN_STATUSES,
  STEP_TYPE,
  TIME_FORMAT,
} from '../../constants';
import { getStopsOrder } from '../../utils/map';
import { calculatePixelBetweenStops, getWidthChart } from '../Mapbox/Utils';
import RouteOrdersTable from '../Tables/RoutePlans/RouteOrdersTable';
import RouteRidersTable from '../Tables/RoutePlans/RouteRidersTable';
import './RoutePlanDetail.scss';
import RoutePlanChart from './RoutePlans/RoutePlanChart';
import StopsMapbox from './RoutePlans/StopsMapBox';
import SwapRider from './RoutePlans/SwapRider';
import usePageView from '../../hooks/usePageView';

const socketURI = process.env.REACT_APP_SOCKET_URI || '';
const socket = socketIOClient(socketURI, {
  transports: ['websocket'],
});

const RoutePlanDetail = ({ query, params, id }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isFetchingRoute = useSelector((state) => state.routePlan.isFetching);
  const action = useSelector((state) => state.routePlan.action);
  const status = useSelector((state) => state.routePlan.status);
  const routePlan = useSelector((state) => state.routePlan.routePlanDetail);

  const riders = routePlan?.riders || [];
  const orders = routePlan?.orders || [];
  const isFetchingRouteRider = useSelector((state) => state.routePlanRider.isFetching);
  const isFetchingRouteOrder = useSelector((state) => state.routePlanOrder.isFetching);
  const type = params?.type;

  const [stops, setStops] = useState([]);
  const [isOptimize, setIsOptimize] = useState(false);
  const [mapData, setMapData] = useState({});

  const [stepsHovered, setStepsHovered] = useState({});
  const [state, setState] = useState({});
  const [solutions, setSolutions] = useState({});
  const [stepCompleted, setStepCompleted] = useState({});
  const [clicked, setClicked] = useState(false);
  const [oneHourPx, setOneHourPx] = useState(ONE_HOUR_PX_DEFAULT);
  const [showMoreRoute, setShowMoreRoute] = useState(false);
  const [routeType, setRouteType] = useState(null);
  const [riderIdsHide, setRiderIdsHide] = useState([]);
  const [riderIdsLock, setRiderIdsLock] = useState([]);
  const [selectRiders, setSelectRiders] = useState([]);
  const [selectOrders, setSelectOrders] = useState([]);
  const [routesChange, setRoutesChange] = useState([]);
  const [stateSwap, setStateSwap] = useState({
    visibleModal: false,
    searchedRoutes: [],
    swapFromRoute: {},
    swapToRoute: {},
  });

  // Emit page view event
  usePageView(window.location.href);

  const handleZoomIn = () => {
    setOneHourPx(oneHourPx + oneHourPx / 10);
  };
  const handleZoomOut = () => {
    setOneHourPx(oneHourPx - oneHourPx / 10);
  };

  const onClickRoute = (route) => {
    if (route?.riderId) {
      setStepsHovered({ [route.riderId]: true });
    } else {
      setStepsHovered({});
    }
    setState({ ...state, stopClicked: {}, routeClicked: route });
  };

  const handleOkSwap = () => {
    dispatch(
      doSwapOrdersToRiderRoutePlan({
        id: routePlan.slug,
        fromRouteId: stateSwap.swapFromRoute.id,
        routeId: stateSwap.swapToRoute.id,
      }),
    );
    setStateSwap({ ...stateSwap, visibleModal: false });
  };

  const handleSelectedRouteSwap = (route) => {
    setStateSwap({ ...stateSwap, swapToRoute: route });
  };

  const handleCancelSwap = () => {
    setStateSwap({ ...stateSwap, visibleModal: false });
  };

  const onSearchRiders = (e) => {
    const searchedRoutes = riders.filter((route) => {
      return (
        route.riderId !== stateSwap.swapFromRoute.riderId &&
        route.user.name.toLowerCase().includes(e.target.value.toLowerCase())
      );
    });
    setStateSwap({ ...stateSwap, searchedRoutes });
  };

  const onClickSwap = (route) => {
    const searchedRoutes = riders.filter((item) => item.riderId !== route.riderId);
    setStateSwap({
      ...stateSwap,
      visibleModal: true,
      searchedRoutes: searchedRoutes,
      swapFromRoute: route,
      swapToRoute: {},
    });
  };

  useEffect(() => {
    if (
      routePlan?.status === ROUTE_PLAN_STATUSES.DRAFT &&
      routePlan?.id == id &&
      type !== 'orders' &&
      type !== 'drivers'
    ) {
      history.push(`/routes/${routePlan.id}/orders`);
    }
  }, [routePlan, id]);

  useEffect(() => {
    if (action === 'optimize' && status === 'succeeded') setIsOptimize(true);
  }, [status, action]);

  useEffect(() => {
    if (routePlan?.riders) formatSolutionsAndMapData(routePlan.riders);
  }, [routePlan, oneHourPx]);

  const formatSolutionsAndMapData = (ds) => {
    const solutionsTmp = {};
    const mapDataTmp = {};
    const stepCompletedTmp = {};
    const newRiderIdsLock = [];
    const sortRoutes = sortBy(ds, 'riderId');
    const orderNonInsideRoute = [];
    sortRoutes.forEach((route) => {
      const steps = calculatePixelBetweenStops(route.steps, route, oneHourPx);
      let newSteps = [];
      let newStepsDone = [];
      if (route.locked) {
        newRiderIdsLock.push(route.riderId);
      }
      if (steps.length > 0) {
        for (let index = 0; index < steps.length; index++) {
          const step = steps[index];
          const width = step.arrivalTime ? getWidthChart(step.arrivalTime, step.finishTime, oneHourPx) : '24px';
          if (!step) return;
          const rider = riders?.find((r) => r.riderId == route.riderId);
          const riderName = rider?.user?.name || '';
          const stepResponse = {
            id: uuidv4(),
            stepId: step.id,
            name: step.type === STEP_TYPE.pickup ? step.order.pickupName : step.order.deliveryName,
            location: step.order.pickupName,
            address: step.order.pickupAddress,
            lat: step.order.pickupAddressLat,
            lng: step.order.pickupAddressLng,
            arrivalTime: step.arrivalTime,
            finishTime: step.finishTime,
            startTime: step.order?.deliveryTimeStart,
            endTime: step.order?.deliveryTimeEnd,
            status: step.status,
            type: step.type,
            distance: step.distance,
            index: step.orderNumber,
            duration: step.duration,
            marginLeft: step.marginLeft,
            logoFile: step.order?.location?.company?.logoThumb || step.order?.location?.company?.logo,
            urlPhoto: routePlan.urlPhoto,
            width,
            orderIds: [step.orderId],
            orderId: step.orderId,
            riderId: route.riderId,
            orderCode: step.order?.orderId,
            orderRef: step.order?.orderRef,
            orderStatus: step.order?.status,
            riderName,
            travelMins: step.travelMins || 0,
            workingMins: step.workingMins || 0,
            waitingMins: step.waitingMins || 0,
          };

          if (step.type === STEP_TYPE.delivery) {
            delete stepResponse.orderIds; // No need
            stepResponse.location = step.order.deliveryName;
            stepResponse.company = step.order?.location?.company?.name || '';
            stepResponse.address = step.order.deliveryAddress;
            stepResponse.lat = step.order.deliveryAddressLat;
            stepResponse.lng = step.order.deliveryAddressLng;
          }

          if (step.type === STEP_TYPE.pickup && index < steps.length - 1) {
            delete stepResponse.orderId; // No need
            delete stepResponse.status; // No need
            delete stepResponse.orderStatus; // No need
            let samePickup = false;
            const orderIds = [...stepResponse.orderIds];
            do {
              const currentNext = steps[index];
              const stepNext = steps[index + 1];
              samePickup =
                stepNext.type === STEP_TYPE.pickup &&
                currentNext.status === stepNext.status &&
                currentNext?.order?.pickupAddress === stepNext?.order?.pickupAddress;
              //currentNext.order.locationId === stepNext.order.locationId;
              if (samePickup) {
                stepResponse.duration += stepNext.duration;
                stepResponse.distance += stepNext.distance;
                stepResponse.travelMins += stepNext.travelMins;
                stepResponse.workingMins += stepNext.workingMins;
                stepResponse.waitingMins = stepNext.waitingMins;
                stepResponse.finishTime = stepNext.finishTime;
                orderIds.push(stepNext.orderId);
                index++;
              }
            } while (samePickup);
            stepResponse.orderIds = orderIds;
          }
          newSteps.push(stepResponse);
        }
      }

      const orderIdsSteps = newSteps.map((n) => n.orderId);
      const orderSteps = [];

      const ordersNonOptimize = sortBy(
        orders.filter((o) => o.riderId === route.riderId && !orderIdsSteps.includes(o.id)),
        (o) => moment(o.completedAt).unix(),
      );
      if (ordersNonOptimize?.length > 0)
        ordersNonOptimize.forEach((op, index) => {
          if (op.status === 'COMPLETED') {
            const rider = riders?.find((r) => r.riderId == route.riderId);
            const riderName = rider?.user?.name || '';
            orderSteps.push({
              id: uuidv4(),
              stepId: null,
              name: op.deliveryName,
              location: op.deliveryName,
              address: op.deliveryAddress,
              lat: op.deliveryAddressLat,
              lng: op.deliveryAddressLng,
              startTime: op?.deliveryTimeStart,
              arrivalTime: moment(op.completedAt).format(TIME_FORMAT),
              finishTime: moment(op.completedAt).add(op.deliveryDuration, 'minutes').format(TIME_FORMAT),
              type: STEP_TYPE.delivery,
              endTime: op?.deliveryTimeEnd,
              status: op.status,
              index: index,
              duration: op.deliveryDuration,
              orderId: op.id,
              riderId: route.riderId,
              completedAt: op.completedAt,
              riderName,
              orderCode: op?.orderId,
              orderStatus: op.status,
            });
          } else {
            orderNonInsideRoute.push(op.id);
          }
        });
      newStepsDone = calculatePixelBetweenStops(orderSteps, route, oneHourPx);

      solutionsTmp[`assign_${route.riderId}`] = {
        steps: [],
        color: COLOR_DEFAULT,
      };
      const color = COLORS[route.riderId % COLORS.length];
      solutionsTmp[route.riderId] = {
        steps: newSteps,
        color,
      };
      stepCompletedTmp[route.riderId] = newStepsDone;
      mapDataTmp[route.riderId] = {
        polyline: route.polyline,
        steps: newStepsDone.concat(newSteps),
        color: color,
        endAddressLat: route.endAddressLat,
        endAddressLng: route.endAddressLng,
        startAddressLat: route.startAddressLat,
        startAddressLng: route.startAddressLng,
      };
    });
    const unassignedStops = sortBy(
      orders.filter((o) => {
        return o.status === ORDER_STATUSES.UNASSIGNED || !o.riderId || orderNonInsideRoute.includes(o.id);
      }),
      (order) => order.locationId,
    );
    const stepsUnassigned = [];
    const indexPickupDone = [];
    for (let index = 0; index < unassignedStops.length; index++) {
      const order = unassignedStops[index];
      let indexPickupUnassigned = index;
      stepsUnassigned.push({
        id: uuidv4(),
        stepId: null,
        name: order.deliveryName,
        location: order.deliveryName,
        address: order.deliveryAddress,
        lat: order.deliveryAddressLat,
        lng: order.deliveryAddressLng,
        startTime: order?.deliveryTimeStart,
        type: STEP_TYPE.delivery,
        endTime: order?.deliveryTimeEnd,
        status: order.status,
        index: index,
        duration: order.deliveryDuration,
        orderId: order.id,
        orderCode: order?.orderId,
      });

      if (!indexPickupDone.includes(index)) {
        const stepResponsePickup = {
          id: uuidv4(),
          stepId: null,
          name: order.pickupName,
          location: order.pickupName,
          address: order.pickupAddress,
          lat: order.pickupAddressLat,
          lng: order.pickupAddressLng,
          startTime: order?.pickupTimeStart,
          type: STEP_TYPE.pickup,
          endTime: order?.pickupTimeEnd,
          index: indexPickupUnassigned,
          duration: order.pickupDuration,
          orderIds: [order.id],
        };
        const orderIds = [...stepResponsePickup.orderIds];
        let samePickup = false;
        do {
          const currentNext = unassignedStops[indexPickupUnassigned];
          const orderNext = unassignedStops[indexPickupUnassigned + 1];
          const nexIndex = indexPickupUnassigned + 1;
          //samePickup = currentNext.locationId === orderNext?.locationId;
          samePickup = currentNext?.pickupAddress === orderNext?.pickupAddress;
          if (samePickup) {
            indexPickupDone.push(nexIndex);
            orderIds.push(orderNext.id);
            indexPickupUnassigned++;
          }
        } while (samePickup);
        stepResponsePickup.orderIds = orderIds;
        stepsUnassigned.push(stepResponsePickup);
      }
    }
    const rsUnassignedStops = {
      steps: stepsUnassigned,
      color: '#606060',
    };
    solutionsTmp['unscheduled'] = rsUnassignedStops;

    mapDataTmp['unscheduled'] = rsUnassignedStops;

    setSolutions(solutionsTmp);
    setStepCompleted(stepCompletedTmp);
    setMapData(mapDataTmp);
    setRiderIdsLock(newRiderIdsLock);
  };

  const onClickStop = (step) => {
    if (step?.id && step?.type) {
      const key = `StepPopover-${step.id}-${step.type.toLowerCase()}`;
      setStepsHovered({ [key]: true });
    } else {
      setStepsHovered({});
    }
    setState({ ...state, stepClicked: step, routeClicked: {} });
  };

  const onClosePopup = () => {
    setStepsHovered({});
    setState({ ...state, stepClicked: { id: 'closed' }, routeClicked: {} });
  };

  useEffect(() => {
    if (orders?.length > 0) {
      let stopsT = [];
      forEach(orders, (order) => {
        stopsT = stopsT.concat(getStopsOrder(order, stopsT));
      });
      setStops(stopsT);
    }
  }, [orders]);

  useEffect(() => {
    socket.connect();
    socket.on('routePlan', (data) => {
      if (data.event === 'optimizing.completed' && data.payload.name == id) {
        console.log('routePlan.optimizing.completed');
        setIsOptimize(false);
        dispatch(doGetRoutePlan({ id }));
        if (type) history.push(`/routes/${id}`);
      }

      if (data.event === 'optimizing.error' && data.payload.name == id) {
        console.log('routePlan.optimizing.error');
        setIsOptimize(false);
        notification.error({ message: 'Optimize failed', style: { top: '52px' } });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  const handleClickRoute = () => {
    if (showContinue && type) {
      history.push(`/routes/` + id);
    } else {
      handleOptimizeRoute();
    }
  };

  const handleOptimizeRoute = () => {
    const params = { id };
    const assigns = {};
    Object.keys(solutions).forEach((k) => {
      if (k.includes('assign_')) {
        const element = solutions[k];
        if (element?.steps?.length > 0) {
          console.log(element);
          const deliveries = element?.steps.filter((s) => s.type === STEP_TYPE.delivery);
          const riderId = k.split('_')[1];
          deliveries.forEach((d) => {
            assigns[d.orderId] = riderId;
          });
        }
      }
    });
    if (!isEmpty(assigns)) params['assigns'] = assigns;
    dispatch(doOptimizeRoutePlan({ ...params }));
  };

  const handleDispatch = async () => {
    try {
      await dispatch(
        doDispatchRoutePlan({
          id,

          type: 'SEND_NOW',
          sendAt: moment().toISOString(),
        }),
      );
      onClosePopup();
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(doGetRoutePlan({ id }));
    }
  };

  const isFetching = isFetchingRoute || isFetchingRouteRider || isFetchingRouteOrder;

  const disabledOptimize =
    !(riders?.length > 0 && orders.length > 0) || !riders.find((r) => !riderIdsLock.includes(r.riderId));

  const showAddRivers = type === 'orders' && riders?.length === 0 && orders?.length !== 0;
  const showAddOrders = type === 'riders' && orders?.length === 0 && riders?.length !== 0;

  // const showOptimize =
  //   !showAddRivers && !showAddOrders && routePlan?.status === ROUTE_PLAN_STATUSES.DRAFT && routePlan?._id === id;
  const showContinue =
    !showAddRivers &&
    !showAddOrders &&
    (routePlan?.status === ROUTE_PLAN_STATUSES.OPTIMIZED || routePlan?.status === ROUTE_PLAN_STATUSES.DISPATCHED) &&
    routePlan?.id == id;
  const breadCrumbs = [
    { text: 'ROUTES', url: `/routes` },
    {
      text: routePlan?.name || '',
    },
  ];

  useEffect(() => {
    dispatch(doGetRoutePlan({ id }));
  }, []);

  useEffect(() => {
    if (action === 'getOne' && ['succeeded', 'failed'].includes(status) && !routePlan) {
      history.push(`/routes`);
    }
  }, [action, status, routePlan]);

  const handleRemoveOrders = async (selectedOrders) => {
    try {
      await dispatch(doRemoveOrdersInsideRoutePlan({ id, ids: selectedOrders?.map((r) => r.id) }));
    } catch (e) {
      console.log(e);
    } finally {
      setSelectRiders([]);
      dispatch(doGetRoutePlan({ id }));
    }
  };

  const handleRemoveRiders = async (selectedRiders) => {
    try {
      await dispatch(doRemoveRidersInsideRoutePlan({ id, ids: selectedRiders?.map((r) => r.id) }));
    } catch (e) {
      console.log(e);
    } finally {
      setSelectRiders([]);
      dispatch(doGetRoutePlan({ id }));
    }
  };

  const handleBulkUpdateOrders = (selectedOrders) => {
    const removeOrderTooltipText =
      selectedOrders.length <= 0
        ? 'Please select one or more orders first'
        : selectedOrders.some((o) => o.status === ORDER_STATUSES.COMPLETED)
          ? 'Cannot remove because one of the selected orders has a completed status'
          : '';

    return selectedOrders.length > 0
      ? {
          title: `${selectedOrders.length} Orders selected`,
          actions: (
            <Space>
              <Tooltip title={removeOrderTooltipText}>
                <Button
                  type="primary"
                  disabled={
                    selectedOrders.length <= 0 || selectedOrders.some((o) => o.status === ORDER_STATUSES.COMPLETED)
                  }
                  onClick={() => handleRemoveOrders(selectedOrders)}
                >
                  Remove
                </Button>
              </Tooltip>
            </Space>
          ),
        }
      : undefined;
  };

  const handleBulkUpdateRiders = (selectedRiders) => {
    return selectedRiders.length > 0
      ? {
          title: `${selectedRiders.length} Riders selected`,
          actions: (
            <Space>
              <Button
                type="primary"
                disabled={selectedRiders.length <= 0}
                onClick={() => handleRemoveRiders(selectedRiders)}
              >
                Remove
              </Button>
            </Space>
          ),
        }
      : undefined;
  };

  return (
    <Spin spinning={isFetching || isOptimize}>
      <div className="RoutePlanContent RoutePlanDetail">
        <div className="ContentLeft">
          <RoutePlanLeft routePlan={routePlan} riders={riders} orders={orders} />
        </div>
        {type ? (
          <div className="Content ContentFull">
            <div className="wrapper">
              <Row>
                <Col span={24}>
                  <div className="ContentMain">
                    <div className="header">
                      <BreadCrumb breadCrumbs={breadCrumbs} />
                    </div>
                    <div className="content">
                      <Row>
                        <Col span={16}>
                          <div className="content">
                            {type === 'riders' && (
                              <RouteRidersTable
                                dataSource={riders}
                                routePlan={routePlan}
                                query={query}
                                bulkUpdate={handleBulkUpdateRiders}
                                defaultSelectedItems={selectRiders}
                                setDefaultSelectedItems={setSelectRiders}
                                isShowBulk={false}
                              />
                            )}
                            {type === 'orders' && (
                              <RouteOrdersTable
                                dataSource={orders}
                                routePlan={routePlan}
                                query={query}
                                riders={riders}
                                bulkUpdate={handleBulkUpdateOrders}
                                defaultSelectedItems={selectOrders}
                                setDefaultSelectedItems={setSelectOrders}
                                isShowBulk={false}
                                mapData={mapData}
                                stepCompleted={stepCompleted}
                              />
                            )}
                          </div>
                        </Col>
                        <Col span={8}>
                          <div className="map">
                            <StopsMapbox mapData={mapData} routePlan={routePlan} />
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        ) : (
          <RoutePlanChart
            mapData={mapData}
            setRoutesChange={setRoutesChange}
            routesChange={routesChange}
            setMapData={setMapData}
            routePlan={routePlan}
            routes={riders}
            stops={stops}
            stepsHovered={stepsHovered}
            setStepsHovered={setStepsHovered}
            state={state}
            setState={setState}
            onClickStop={onClickStop}
            onClosePopup={onClosePopup}
            solutions={solutions}
            stepCompleted={stepCompleted}
            setSolutions={setSolutions}
            clicked={clicked}
            setClicked={setClicked}
            oneHourPx={oneHourPx}
            handleZoomIn={handleZoomIn}
            handleZoomOut={handleZoomOut}
            onClickRoute={onClickRoute}
            setShowMoreRoute={setShowMoreRoute}
            showMoreRoute={showMoreRoute}
            routeType={routeType}
            setRouteType={setRouteType}
            routeClicked={state.routeClicked}
            stepClicked={state.stepClicked}
            orders={orders}
            setIsOptimize={setIsOptimize}
            riderIdsHide={riderIdsHide}
            setRiderIdsHide={setRiderIdsHide}
            riderIdsLock={riderIdsLock}
            setRiderIdsLock={setRiderIdsLock}
            onClickSwap={onClickSwap}
          />
        )}
        <div className="ContentFooter">
          <Space>
            <Button
              disabled={disabledOptimize}
              className="btn-add-to-route"
              size="large"
              key="AddToRoute"
              type="primary"
              onClick={handleClickRoute}
            >
              {showContinue && type ? 'Continue to Route' : 'Optimize Route'}
              <ReactSVG className="icon" src="/icons/continue.svg" />
            </Button>
            {!type && (
              <Button size="large" className="btn-dispatch-route" onClick={handleDispatch}>
                {routePlan?.status === ROUTE_PLAN_STATUSES.DISPATCHED ? 'Dispatch Update' : 'Dispatch to Drivers'}
                <ReactSVG className="icon" src="/icons/dispatch-to-driver.svg" />
              </Button>
            )}
          </Space>
        </div>
      </div>
      <SwapRider
        state={stateSwap}
        handleOk={handleOkSwap}
        handleCancel={handleCancelSwap}
        handleSelectedRoute={handleSelectedRouteSwap}
        onSearchRiders={onSearchRiders}
      />
    </Spin>
  );
};

export default withPage(RoutePlanDetail);
